/**
 * Resolves a text item's (family, weight, style) to an embeddable
 * `@font-face{...}` CSS string (Google Font TTF or custom upload, base64
 * inlined), for splicing into generated SVG so it renders correctly outside
 * the live editor page (canvas-rasterized thumbnails, raw SVG download,
 * server-composed export SVG).
 */
import type { Pinia } from 'pinia';
import { useConfigStore } from '../stores/configStore';
import { useFonts } from './useFonts';
import {
  GENERIC_FAMILIES,
  normalizeWeight,
  normalizeStyle,
  resolveFontFaceCss,
  createFontBase64Cache,
  type FontWeight,
  type FontStyle,
} from '../../shared/fontEmbedding';
import { veyopAjaxUrl } from '@core/veyopRuntime';

let _getFontBase64: ((url: string) => Promise<string>) | null = null;
/** Base64 fetches are session-lived and identical across every consumer/view, so a single cache is shared regardless of how many times this composable is instantiated. */
function sharedGetFontBase64(): (url: string) => Promise<string> {
  if (!_getFontBase64) _getFontBase64 = createFontBase64Cache();
  return _getFontBase64;
}

const cssCache = new Map<string, Promise<string>>();

export function useFontEmbedding(pinia?: Pinia | null) {
  const config = pinia ? useConfigStore(pinia) : useConfigStore();
  const { fonts, googleFontSpecsRaw, loadFontCatalog } = useFonts(pinia);

  /** Resolve+cache the `@font-face` CSS for one (family, weight, style) usage. Returns '' for generic/system fonts or on any resolution failure. */
  async function getEmbeddableFontFaceCss(
    family: string,
    weight: FontWeight | string = 'normal',
    style: FontStyle | string = 'normal',
  ): Promise<string> {
    const trimmed = family.trim();
    if (!trimmed || GENERIC_FAMILIES.has(trimmed.toLowerCase())) return '';

    const normWeight = normalizeWeight(String(weight));
    const normStyle = normalizeStyle(String(style));
    const key = `${trimmed.toLowerCase()}|${normWeight}|${normStyle}`;

    let pending = cssCache.get(key);
    if (!pending) {
      pending = (async () => {
        await loadFontCatalog();
        const { css } = await resolveFontFaceCss(
          { family: trimmed, weight: normWeight, style: normStyle },
          {
            catalog: fonts.value,
            googleFontSpecs: googleFontSpecsRaw.value,
            googleFontTtfUrl: veyopAjaxUrl('google_font_ttf', {}, config.data.veyopUrl),
            pluginBaseUrl: config.data.veyopUrl,
            getFontBase64: sharedGetFontBase64(),
          },
        );
        return css;
      })();
      cssCache.set(key, pending);
    }
    return pending;
  }

  /** Best-effort prefetch/warm-cache for every distinct font usage in a set of design items — not awaited by callers that don't need the result, just primes the cache. */
  async function prefetchEmbeddableFonts(
    items: Array<{
      type?: string;
      fontFamily?: unknown;
      fontWeight?: unknown;
      fontStyle?: unknown;
    }>,
  ): Promise<void> {
    const usages = new Map<string, { family: string; weight: string; style: string }>();
    for (const item of items) {
      if (item?.type !== 'text') continue;
      const family = String(item.fontFamily ?? '').trim();
      if (!family) continue;
      const weight = String(item.fontWeight ?? 'normal');
      const style = String(item.fontStyle ?? 'normal');
      usages.set(`${family.toLowerCase()}|${weight}|${style}`, { family, weight, style });
    }
    await Promise.allSettled(
      [...usages.values()].map((u) => getEmbeddableFontFaceCss(u.family, u.weight, u.style)),
    );
  }

  return { getEmbeddableFontFaceCss, prefetchEmbeddableFonts };
}
